/**
 * @file components/staff-profile/BasicInfoTab.tsx
 * @description 員工基本信息分頁：帳號唯讀區 + 個人資料可編輯表單。
 *
 * 兩大區塊：
 *  1. 帳號信息（唯讀）：姓名、信箱、電話、角色，來自 GET /api/settings/staff/:id
 *  2. 個人資料（可編輯 Form）：員工編號、性別、出生日期、證件、地址、
 *     合約來源、所屬部門、學歷（Form.List）、工作經歷、培訓記錄、備注
 *     保存 → PUT /api/settings/staff/:id/profile
 */

import { Button, Col, DatePicker, Divider, Form, Input, Row, Select, Space, Spin, Tag } from 'antd';
import { message } from '@/lib/antd-app';
import { PlusCircle, Trash2, Save, User2, FileText } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { GENDER_LABELS, USER_ROLE_LABELS } from '@zhcb/shared';
import { apiClient } from '@/lib/api-client';
import { SectionCard, DataField, DataFieldGrid } from '@/components/shell';
import { useContractSources, useDepartments } from '@/hooks/useClientLookups';
import { useRoleList } from '@/services/roles';
import { color, text, weight } from '@/styles/tokens';

// ─── 型別 ─────────────────────────────────────────────────────────────────────

/** 帳號信息（來自 GET /api/settings/staff/:id） */
interface StaffUser {
  id: number;
  name: string;
  username: string;
  email: string;
  phone: string | null;
  /** 主角色代碼（動態 RBAC 後為 string，含自定義角色代碼） */
  primaryRole: string;
  /** 角色代碼清單（動態 RBAC 後為 string[]） */
  roles: string[];
  isActive: boolean;
}

/** 學歷條目 */
interface EducationEntry {
  school: string;
  major?: string;
  degree?: string;
}

/** 員工完整資料表單值 */
interface ProfileFormValues {
  employeeNo?: string | null;
  gender?: 'male' | 'female' | 'other' | null;
  birthDate?: dayjs.Dayjs | null;
  idType?: string | null;
  idNumber?: string | null;
  address?: string | null;
  contractSource?: string | null;
  departments?: string[];
  education?: EducationEntry[];
  workExperience?: string | null;
  trainingRecord?: string | null;
  notes?: string | null;
}

/** 後端回傳的 profile 資料（所有欄位可空） */
export interface StaffProfile {
  employeeNo?: string | null;
  gender?: string | null;
  birthDate?: string | null;
  idType?: string | null;
  idNumber?: string | null;
  address?: string | null;
  contractSource?: string | null;
  departments?: string[] | null;
  education?: EducationEntry[] | null;
  workExperience?: string | null;
  trainingRecord?: string | null;
  notes?: string | null;
}

// ─── 組件 Props ───────────────────────────────────────────────────────────────

export interface BasicInfoTabProps {
  /** 員工 userId（路由參數） */
  userId: number;
  /** 父層 profile 查詢已載入的 profile 片段（可能為 null） */
  profile: StaffProfile | null;
}

// ─── 主組件 ──────────────────────────────────────────────────────────────────

/**
 * 基本信息 Tab 主組件。
 * 用同一個查詢 key 取得帳號信息，並在頁面初始化時把 profile 填入表單。
 */
export function BasicInfoTab({ userId, profile }: BasicInfoTabProps) {
  const qc = useQueryClient();
  const [form] = Form.useForm<ProfileFormValues>();

  /** 帳號信息查詢（唯讀，用於展示姓名/信箱/角色等） */
  const { data: userData, isLoading: userLoading } = useQuery<{ item: StaffUser }>({
    queryKey: ['staff-user', userId],
    queryFn: () => apiClient.get(`/api/settings/staff/${userId}`),
  });

  /** 動態角色列表（Task 12）：用於自定義角色的 DB name 回落 */
  const { data: rolesData } = useRoleList();
  /** DB 角色 code→name 映射（自定義角色顯示名稱回落） */
  const dbRoleNameMap = new Map((rolesData?.items ?? []).map((r) => [r.code, r.name]));

  /** 合約來源字典（activeOnly） */
  const { data: contractSourcesData } = useContractSources({ activeOnly: true });
  /** 部門字典（activeOnly） */
  const { data: departmentsData } = useDepartments({ activeOnly: true });

  const user = userData?.item;

  /** 儲存 Profile 標量欄位 Mutation */
  const saveProfile = useMutation({
    mutationFn: async (values: ProfileFormValues) => {
      // 出生日期由 dayjs 物件轉為 'YYYY-MM-DD' 字串，或 null
      const birthDate = values.birthDate ? values.birthDate.format('YYYY-MM-DD') : null;
      return apiClient.put(`/api/settings/staff/${userId}/profile`, {
        ...values,
        birthDate,
        // 確保空陣列轉為 null，避免 Zod 校驗產生 undefined 問題
        education: values.education?.length ? values.education : null,
        departments: values.departments?.length ? values.departments : null,
      });
    },
    onSuccess: () => {
      message.success('基本資料已儲存');
      void qc.invalidateQueries({ queryKey: ['staff-profile', userId] });
    },
    onError: (err) => {
      message.error(err instanceof Error ? err.message : '儲存失敗');
    },
  });

  /** 計算表單初始值（Profile → 表單格式） */
  const initialValues: ProfileFormValues = {
    employeeNo: profile?.employeeNo ?? null,
    gender: (profile?.gender as ProfileFormValues['gender']) ?? null,
    // ISO date 字串轉 dayjs 物件供 DatePicker 使用
    birthDate: profile?.birthDate ? dayjs(profile.birthDate) : null,
    idType: profile?.idType ?? null,
    idNumber: profile?.idNumber ?? null,
    address: profile?.address ?? null,
    contractSource: profile?.contractSource ?? null,
    departments: profile?.departments ?? [],
    education: profile?.education ?? [],
    workExperience: profile?.workExperience ?? null,
    trainingRecord: profile?.trainingRecord ?? null,
    notes: profile?.notes ?? null,
  };

  // ─── 帳號信息唯讀渲染 ─────────────────────────────────────────────

  const accountSection = (
    <SectionCard title="帳號信息" icon={<User2 size={14} />} variant="subtle">
      {userLoading ? (
        <Spin size="small" />
      ) : user ? (
        <DataFieldGrid columns={2}>
          <DataField
            label="登入帳號"
            value={
              <span style={{ fontFamily: 'monospace', color: color.text.secondary }}>
                {user.username}
              </span>
            }
          />
          <DataField
            label="姓名"
            value={<span style={{ fontWeight: weight.medium }}>{user.name}</span>}
          />
          <DataField
            label="信箱"
            value={<span style={{ fontFamily: 'monospace', fontSize: text.sm }}>{user.email}</span>}
          />
          <DataField label="電話" value={user.phone ?? null} />
          <DataField
            label="角色"
            value={
              <Space size={4} wrap>
                {(user.roles?.length ? user.roles : [user.primaryRole]).map((r) => (
                  <Tag
                    key={r}
                    color={
                      r === 'admin'
                        ? 'red'
                        : r === 'manager'
                          ? 'orange'
                          : r === 'auditor'
                            ? 'green'
                            : 'blue'
                    }
                  >
                    {((USER_ROLE_LABELS as Record<string, string | undefined>)[r] ??
                      dbRoleNameMap.get(r) ??
                      r) + (r === user.primaryRole ? '（主）' : '')}
                  </Tag>
                ))}
              </Space>
            }
          />
          <DataField
            label="狀態"
            value={
              <Tag color={user.isActive ? 'success' : 'default'}>
                {user.isActive ? '啟用' : '停用'}
              </Tag>
            }
          />
        </DataFieldGrid>
      ) : (
        <span style={{ color: color.text.muted }}>無法載入帳號信息</span>
      )}
    </SectionCard>
  );

  // ─── 可編輯 Profile 表單 ──────────────────────────────────────────

  const profileSection = (
    <SectionCard
      title="個人資料"
      icon={<FileText size={14} />}
      actions={
        <Button
          type="primary"
          size="small"
          icon={<Save size={13} />}
          loading={saveProfile.isPending}
          onClick={() => void form.validateFields().then((values) => saveProfile.mutate(values))}
        >
          儲存
        </Button>
      }
    >
      <Form<ProfileFormValues>
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={(values) => saveProfile.mutate(values)}
        style={{ marginTop: 4 }}
      >
        {/* 第一行：員工編號 + 性別 + 出生日期 */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="employeeNo" label="員工編號">
              <Input placeholder="如 EMP-001" maxLength={50} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="gender" label="性別">
              <Select
                placeholder="請選擇"
                allowClear
                options={Object.entries(GENDER_LABELS).map(([v, l]) => ({ value: v, label: l }))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="birthDate" label="出生日期">
              <DatePicker style={{ width: '100%' }} placeholder="選擇日期" format="YYYY-MM-DD" />
            </Form.Item>
          </Col>
        </Row>

        {/* 第二行：證件類型 + 證件號碼 */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="idType" label="證件類型">
              <Input placeholder="如 身份證、護照" maxLength={30} />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item name="idNumber" label="證件號碼">
              <Input placeholder="證件號碼" maxLength={50} />
            </Form.Item>
          </Col>
        </Row>

        {/* 通訊地址 */}
        <Form.Item name="address" label="通訊地址">
          <Input placeholder="聯絡地址" maxLength={255} />
        </Form.Item>

        {/* 合約來源 + 服務部門 */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="contractSource" label="合約來源">
              <Select
                placeholder="請選擇合約來源"
                allowClear
                showSearch
                filterOption={(input, opt) =>
                  ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={(contractSourcesData?.items ?? []).map((item) => ({
                  value: item.name,
                  label: item.name,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item name="departments" label="服務部門">
              <Select
                mode="multiple"
                placeholder="請選擇所屬部門（可多選）"
                allowClear
                showSearch
                filterOption={(input, opt) =>
                  ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={(departmentsData?.items ?? []).map((item) => ({
                  value: item.name,
                  label: item.name,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <div
          style={{
            fontSize: text.sm,
            color: color.text.tertiary,
            margin: '0 0 12px 0',
            fontWeight: 500,
            borderBottom: `1px solid ${color.border.subtle}`,
            paddingBottom: 8,
          }}
        >
          學歷記錄
        </div>

        {/* 學歷 Form.List：可動態增減多行（學校 + 科系 + 學位） */}
        <Form.List name="education">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Row key={key} gutter={12} align="middle" style={{ marginBottom: 8 }}>
                  <Col span={8}>
                    <Form.Item
                      {...restField}
                      name={[name, 'school']}
                      rules={[{ required: true, message: '請填寫學校名稱' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input placeholder="學校名稱" maxLength={100} />
                    </Form.Item>
                  </Col>
                  <Col span={7}>
                    <Form.Item {...restField} name={[name, 'major']} style={{ marginBottom: 0 }}>
                      <Input placeholder="科系（選填）" maxLength={100} />
                    </Form.Item>
                  </Col>
                  <Col span={7}>
                    <Form.Item {...restField} name={[name, 'degree']} style={{ marginBottom: 0 }}>
                      <Input placeholder="學位（如 學士、碩士）" maxLength={50} />
                    </Form.Item>
                  </Col>
                  <Col span={2} style={{ textAlign: 'center' }}>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<Trash2 size={13} />}
                      onClick={() => remove(name)}
                    />
                  </Col>
                </Row>
              ))}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  icon={<PlusCircle size={14} />}
                  size="small"
                >
                  新增學歷
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Divider style={{ margin: '4px 0 16px 0' }} />

        {/* 工作經歷 */}
        <Form.Item name="workExperience" label="工作經歷">
          <Input.TextArea
            placeholder="請填寫工作經歷（自由文字）"
            rows={3}
            maxLength={2000}
            showCount
          />
        </Form.Item>

        {/* 培訓記錄 */}
        <Form.Item name="trainingRecord" label="培訓記錄">
          <Input.TextArea
            placeholder="請填寫培訓記錄（自由文字）"
            rows={3}
            maxLength={2000}
            showCount
          />
        </Form.Item>

        {/* 備注 */}
        <Form.Item name="notes" label="備注">
          <Input.TextArea placeholder="其他備注（自由文字）" rows={2} maxLength={500} showCount />
        </Form.Item>

        {/* 底部儲存按鈕（表單 submit） */}
        <Form.Item style={{ marginBottom: 0 }}>
          <Button
            type="primary"
            htmlType="submit"
            icon={<Save size={14} />}
            loading={saveProfile.isPending}
          >
            儲存基本資料
          </Button>
        </Form.Item>
      </Form>
    </SectionCard>
  );

  return (
    <Space direction="vertical" size={16} style={{ display: 'flex' }}>
      {accountSection}
      {profileSection}
    </Space>
  );
}
